import { dataset, filmwebProjectId, projectId } from "@/lib/env";
import { getCMSImageAttributes, type ImageProps } from "@/lib/imgFuncs";
import { SanityImageCrop, SanityImageHotspot } from "@/sanity/sanity.types";
import { type BygdekinoenModifiedImageDoument } from "@/sanity/types/DeRefImageField";
import {createImageUrlBuilder} from "@sanity/image-url";
import { ImageUrlBuilder } from "@sanity/image-url";
import { ImageCrop, ImageHotspot } from "@sanity/types";
import { preload } from "react-dom";

const bkImgUrlBuilder = createImageUrlBuilder({ projectId, dataset });
const fwImgUrlBuilder = createImageUrlBuilder({ projectId: filmwebProjectId, dataset });

//#region [Props]
type CMSImageProps = ImageProps & {
    image: BygdekinoenModifiedImageDoument | null | undefined;
};
//#endregion


//#region [Component]
export default function CMSImage({ image, altText, ...props }: CMSImageProps) {
    if (!image?.image?.asset?._ref) {
        return null;
    }

    const assetRef = image.image.asset._ref;
    const _altText = altText ?? image.altText ?? image.title!;
    return <CMSAssetRefImage assetRef={assetRef} altText={_altText} imageCrop={image.image?.crop} imageHotspot={image.image?.hotspot} imgUrlBuilder={bkImgUrlBuilder} {...props} />
}
//#endregion

//#region [Props]
type CMSAssetRefImageProps = {
    assetRef: string;
    imageHotspot?: SanityImageHotspot | ImageHotspot;
    imageCrop?: SanityImageCrop | ImageCrop;
    altText: string;
    imgUrlBuilder?: ImageUrlBuilder;
    customCrop?: boolean;
} & Omit<ImageProps, "altText">;

type FilmwebCMSAssetRefImageProps = {
    assetRef: string;
    imageCrop?: ImageCrop;
    imageHotspot?: ImageHotspot;
    altText: string;
    customCrop?: boolean;
} & Omit<ImageProps, "altText">
//#endregion

//#region [Component]
export function FilmwebCMSImage(props: FilmwebCMSAssetRefImageProps) {
    return <CMSAssetRefImage imgUrlBuilder={fwImgUrlBuilder} {...props} />
}
//#endregion

//#region [Component]
export function CMSAssetRefImage({ assetRef, priority = false, altText, className, sizes, width, height, unoptimized = false, crop = false, isPosterAspect, imageHotspot, imageCrop, imgUrlBuilder = bkImgUrlBuilder, customCrop = false }: CMSAssetRefImageProps) {
    const img = getCMSImageAttributes({ assetRef, imgUrlBuilder, width, height, sizes, unoptimized, crop, isPosterAspect, imageHotspot, imageCrop, customCrop });
    if (priority) {
        preload(img.src, { as: "image", imageSrcSet: img.srcSet, imageSizes: img.sizes });
    }

    // eslint-disable-next-line @next/next/no-img-element
    return <img src={img.src} srcSet={img.srcSet} sizes={img.sizes} width={img.width} height={img.height} alt={altText} className={className} loading={priority ? undefined : "lazy"} decoding="async" style={{ color: "transparent" }} />;
}
//#endregion
